/**
 * Entry point.
 *
 * Retries are off. On a monitoring dashboard a failed fetch is itself a signal, and
 * silently retrying it three times turns "the API is down" into a page that just looks
 * slow. The poll interval is the retry.
 */

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import App from './App'
import { ApiError } from './api/client'
import './index.css'

// One QueryClient, and a cache-level error handler that re-checks auth whenever any
// query comes back 401. That is how a session expiring mid-poll drops the app to the
// login screen: the poll fails 401, this refetches auth-status, which flips `gated`, and
// App swaps in LoginPage - without a manual reload.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error instanceof ApiError && error.status === 401 && query.queryKey[0] !== 'auth-status') {
        void queryClient.invalidateQueries({ queryKey: ['auth-status'] })
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
      // The backend's poll loop is the source of freshness; a window refocus should not
      // trigger a fresh round of probes to every monitored host. (Auth-status opts back
      // into focus refetch in its own hook.)
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
    mutations: { retry: false },
  },
})

const container = document.getElementById('root')
if (!container) throw new Error('Missing #root')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
